import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Search, X } from 'lucide-react'
import type { Column } from '@tanstack/react-table'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import type { FilterValue } from '@/shared/lib/api/api-types'

interface NumberFilterProps<TData, TValue> {
  column: Column<TData, TValue>
  onClose: () => void
}

const NUMBER_TYPES = [
  { value: 'equals', labelKey: 'dataTable.operators.equals' },
  { value: 'notEqual', labelKey: 'dataTable.operators.notEqual' },
  { value: 'greaterThan', labelKey: 'dataTable.operators.greaterThan' },
  { value: 'greaterThanOrEqual', labelKey: 'dataTable.operators.greaterThanOrEqual' },
  { value: 'lessThan', labelKey: 'dataTable.operators.lessThan' },
  { value: 'lessThanOrEqual', labelKey: 'dataTable.operators.lessThanOrEqual' },
  { value: 'inRange', labelKey: 'dataTable.operators.between' },
]

export function NumberFilter<TData, TValue>({ column, onClose }: NumberFilterProps<TData, TValue>) {
  const { t } = useTranslation()
  const current = column.getFilterValue() as Extract<FilterValue, { filterType: 'number' }> | undefined
  const [type, setType] = useState(current?.type ?? 'equals')
  const [value, setValue] = useState(String(current?.filter ?? ''))
  const [valueTo, setValueTo] = useState(String(current?.filterTo ?? ''))

  function apply() {
    const num = parseFloat(value)
    if (isNaN(num)) {
      column.setFilterValue(undefined)
      onClose()
      return
    }
    const filter: Extract<FilterValue, { filterType: 'number' }> = {
      filterType: 'number',
      type,
      filter: num,
      ...(type === 'inRange' && !isNaN(parseFloat(valueTo))
        ? { filterTo: parseFloat(valueTo) }
        : {}),
    }
    column.setFilterValue(filter)
    onClose()
  }

  return (
    <div className="flex flex-col gap-2 p-2 w-52">
      <Select value={type} onValueChange={setType}>
        <SelectTrigger className="h-7 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {NUMBER_TYPES.map((option) => (
            <SelectItem key={option.value} value={option.value} className="text-xs">
              {t(option.labelKey)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="number"
        className="h-7 text-xs"
        placeholder={t('dataTable.value')}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
      />
      {type === 'inRange' && (
        <Input
          type="number"
          className="h-7 text-xs"
          placeholder={t('dataTable.to')}
          value={valueTo}
          onChange={(e) => setValueTo(e.target.value)}
        />
      )}
      <div className="flex gap-1">
        <Button size="sm" className="h-6 flex-1 text-xs" onClick={apply}>
          <Search className="mr-1 h-3 w-3" /> {t('dataTable.apply')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs"
          onClick={() => { column.setFilterValue(undefined); onClose() }}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}
